import { useCallback, useMemo, useState } from 'react';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { FetchAiOnboardingModalDataV2Document } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { COLORS } from '@/styles/tokens/colors';
import { PathsOf } from '@/types/subform';

type LiabilityEntityField = 'recipientId' | 'issuerId';

interface LiabilityFieldsRowProps {
  index: number;
}

export function LiabilityFieldsRow({ index }: LiabilityFieldsRowProps) {
  const { control, setValue } = useFormContext<AIOnboardingModalFormShape>();

  const { styles, columns } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.liabilities;

  const { entities } = useHouseholdDetailsContext();
  const {
    household: { id: householdId },
  } = useAIOnboardingModalContext();

  const entitySelectOptions = useMemo(
    () => [
      {
        value: NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
        display: 'External',
      },
      ...(entities || []).map((entity) => ({
        value: entity.id,
        display: entity.subtype.displayName,
      })),
    ],
    [entities]
  );

  // Tracks which entity field triggered the creation modal; null when closed
  const [activeEntityField, setActiveEntityField] =
    useState<LiabilityEntityField | null>(null);

  const handleAfterCreateEntity = useCallback(
    (newEntityId: string) => {
      if (!activeEntityField) return;
      setValue(
        `liabilities.${index}.${activeEntityField}` as const satisfies PathsOf<AIOnboardingModalFormShape>,
        newEntityId
      );
      setActiveEntityField(null);
    },
    [activeEntityField, index, setValue]
  );

  return (
    <>
      {activeEntityField && (
        <CreateEntityShortFormModal
          isOpen={!!activeEntityField}
          onClose={() => setActiveEntityField(null)}
          householdId={householdId}
          isOnboardingFlow={false}
          onAfterCreate={handleAfterCreateEntity}
          ignoredQueryDocuments={[FetchAiOnboardingModalDataV2Document]}
        />
      )}
      <FormLayoutRow sx={{ gap: styles.columnPaddingHorizontal }}>
        <FormLayoutItem width={columns.name.columnWidth}>
          <FormAwareTextInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `liabilities.${index}.name` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.name.columnHeader}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.recipientId.columnWidth}>
          <FormAwareTypeaheadSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `liabilities.${index}.recipientId` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.recipientId.columnHeader}
            options={entitySelectOptions}
            hideLabel
            required
            showErrorBeforeSubmit
            addNewOption={{
              text: 'Add entity',
              onClick: () => setActiveEntityField('recipientId'),
            }}
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.issuerId.columnWidth}>
          <FormAwareTypeaheadSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `liabilities.${index}.issuerId` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.issuerId.columnHeader}
            options={entitySelectOptions}
            hideLabel
            required
            showErrorBeforeSubmit
            addNewOption={{
              text: 'Add entity',
              onClick: () => setActiveEntityField('issuerId'),
            }}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow
        sx={{
          marginTop: -2,
          gap: styles.columnPaddingHorizontal,
          backgroundColor: COLORS.GRAY['100'],
          padding: 1,
          borderRadius: 0.75,
        }}
      >
        <FormLayoutItem width={columns.outstandingBalance.columnWidth}>
          <FormAwareCurrencyInput<AIOnboardingModalFormShape>
            isDecimalJSInput
            control={control}
            fieldName={
              `liabilities.${index}.outstandingBalance` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.outstandingBalance.columnSubheader ?? ''}
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </>
  );
}
